import React, { useState, useEffect, useMemo } from 'react';
import { send_component_save_request, send_request } from '../../utils/Request';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { Alert, Grid } from '@mui/material';
import useLoopThroughListStore, { getLoopThroughListStore } from './useLoopThroughListStore';
import ItemLTL from './Components/ItemLTL';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import CustomPaper from '../common/CustomPaper';
import {
  LOOP_THROUGH_LIST_MAX_COMPONENTS,
  LOOP_THROUGH_LIST_MAX_ITEMS,
} from './LoopThroughListConstants';
import './LoopThroughListTemplate.css';

const LoopThroughListTemplate = (props) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getLoopThroughListStore(props.component_id), [props.component_id]);

  const componentName = useProjectStore(s => s.componentName);

  useEffect(() => {
    if (props.component_id !== undefined) {
      // If store was prefilled from canvas load, skip the REST fetch
      if (store.getState().loaded) {
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
        setLoading(false);
        props.setChildTemplateLoaded(true);
        return;
      }

      setLoading(true);
      send_request(
        `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
        '',
        {}
      )
        .then(response => {
          if (response && response.data) {
            const { components } = response.data;
            if (components) {
              store.getState().setComponentData(components[0]);
              store.setState({ loaded: true });
              props.onComponentDescriptionChanged(components[0].description);
              props.onComponentNameChanged(store.getState().name);
              props.onLastModifiedChanged(store.getState().lastModified);
              setLoading(false);
            }
          }

          setLoading(false);
          props.setChildTemplateLoaded(true);
        })
        .catch(err => {
          throw Error(err.message);
        });
    }
  }, []);

  useEffect(() => {
    store.getState().setComponentName(componentName);
  }, [componentName]);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder['selected']) return;
        const { style, key, isNotPlaceholder } = placeholder['selected'];

        insertPlaceholder(style.default, key, style, isNotPlaceholder);
      }
    );
    return unsub;
  }, []);

  const insertPlaceholder = (label, text, style, isNotPlaceholder) => {
    const template = store.getState().getTemplate();
    const index = template.position;
    const field = template.field;
    const posiCondition = template.posiCondition;
    const posiConditionIsMet = template.posiConditionIsMet;

    if (isNotPlaceholder !== true) {
      text = '${' + text + '}';
    }

    if (text) {
      if (posiCondition != null && posiCondition >= 0) {
        store.getState().setCondition(text, index, posiCondition, 'value', 'conditions');
      } else if (posiConditionIsMet != null && posiConditionIsMet >= 0) {
        store.getState().setCondition(
          text,
          index,
          posiConditionIsMet,
          'value',
          'conditionIsMet'
        );
      } else {
        store.getState().setComponent(text, index, field);
      }
    }
  };

  const handleKeyChanges = (err) => {
    setError(err);
  };

  const data = store(s => s.data);

  if (!loading) {
    return (
      <CustomPaper>
        <Grid container xs={12} direction={'column'}>
          <Grid item xs={12}>
            <Alert className="loop-through-list__limit-alert" severity="info">
              Maximum {LOOP_THROUGH_LIST_MAX_ITEMS} items per list. Maximum{' '}
              {LOOP_THROUGH_LIST_MAX_COMPONENTS} Split into Threads components per workflow. Split
              into Threads cannot be nested inside another Split into Threads component.
            </Alert>
          </Grid>
          <ItemLTL
            component={{ key: data.iteratorKey, list: data.list || [] }}
            index={0}
            availablePlaceholders={props.availablePlaceholders}
            handleKeyChanges={handleKeyChanges}
            onPlaceholderSelected={props.onPlaceholderSelected}
          />
        </Grid>
      </CustomPaper>
    );
  } else return <IndividualComponentLoader />;
};

export default LoopThroughListTemplate;
